import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, IconButton, Button, Tooltip, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { DARK_MODE } from '../../constants';
import LockIcon from '@mui/icons-material/Lock';
import { useResolutionScale } from '../../hooks/useResolutionScale';
import { Document, Page, pdfjs } from 'react-pdf';
import styles from './ModalDialog.module.scss';
import clsx from 'clsx';
import ReLogin from '../ReLogin/ReLogin';
import { axiosInstance } from '../../api/api';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArticleIcon from '@mui/icons-material/Article';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DownloadIcon from '@mui/icons-material/Download';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DescriptionIcon from '@mui/icons-material/Description';
import MenuIcon from '@mui/icons-material/Menu';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { encryptStationPassword } from '../../utils/EncryptionUtils';
import Papa from 'papaparse';
import mammoth from 'mammoth';
import SpreadsheetViewer from './SpreadsheetViewer';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const WAVE_HEIGHTS = [30, 50, 40, 60, 35, 55, 25, 65, 45, 70, 30, 50, 40, 55, 35, 60, 25, 45, 55, 35];
const WAVE_DURATIONS = ['0.8s', '1.1s', '0.9s', '1.3s', '0.7s', '1.0s', '1.2s', '0.85s', '1.15s', '0.95s', '1.05s', '0.75s', '1.25s', '0.9s', '1.1s', '0.8s', '1.3s', '0.95s', '1.0s', '0.85s'];

const getFileIcon = (fileName, size = 36) => {
	const ext = fileName?.slice(fileName.lastIndexOf('.')).toLowerCase();
	if (['.mp4', '.webm', '.ogg', '.mov'].includes(ext)) return <VideoFileIcon sx={{ fontSize: size, color: '#e85d4a' }} />;
	if (['.mp3', '.wav', '.aac', '.flac', '.m4a'].includes(ext)) return <AudioFileIcon sx={{ fontSize: size, color: '#a855f7' }} />;
	if (['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'].includes(ext)) return <ImageIcon sx={{ fontSize: size, color: '#2ecc71' }} />;
	if (['.xlsx', '.xls', '.xlsm', '.csv'].includes(ext)) return <TableChartIcon sx={{ fontSize: size, color: '#27ae60' }} />;
	if (ext === '.pdf') return <ArticleIcon sx={{ fontSize: size, color: '#e74c3c' }} />;
	if (['.doc', '.docx'].includes(ext)) return <DescriptionIcon sx={{ fontSize: size, color: '#2b579a' }} />;
	if (['.ppt', '.pptx'].includes(ext)) return <SlideshowIcon sx={{ fontSize: size, color: '#d24726' }} />;
	return <InsertDriveFileIcon sx={{ fontSize: size, color: '#3b6fcf' }} />;
};

const EXT_COLOR_MAP = {
	'.pdf': { from: '#ef4444', to: '#dc2626' },
	'.mp4': { from: '#8b5cf6', to: '#7c3aed' },
	'.mov': { from: '#8b5cf6', to: '#7c3aed' },
	'.webm': { from: '#8b5cf6', to: '#7c3aed' },
	'.ogg': { from: '#8b5cf6', to: '#7c3aed' },
	'.mp3': { from: '#a855f7', to: '#9333ea' },
	'.wav': { from: '#a855f7', to: '#9333ea' },
	'.aac': { from: '#a855f7', to: '#9333ea' },
	'.flac': { from: '#a855f7', to: '#9333ea' },
	'.m4a': { from: '#a855f7', to: '#9333ea' },
	'.xlsx': { from: '#22c55e', to: '#16a34a' },
	'.xls': { from: '#22c55e', to: '#16a34a' },
	'.xlsm': { from: '#22c55e', to: '#16a34a' },
	'.csv': { from: '#10b981', to: '#059669' },
	'.doc': { from: '#2b579a', to: '#1e3f78' },
	'.docx': { from: '#2b579a', to: '#1e3f78' },
	'.ppt': { from: '#d24726', to: '#b33a1e' },
	'.pptx': { from: '#d24726', to: '#b33a1e' },
	'.jpg': { from: '#06b6d4', to: '#0891b2' },
	'.jpeg': { from: '#06b6d4', to: '#0891b2' },
	'.png': { from: '#06b6d4', to: '#0891b2' },
	'.gif': { from: '#06b6d4', to: '#0891b2' },
	'.bmp': { from: '#06b6d4', to: '#0891b2' },
	'.webp': { from: '#06b6d4', to: '#0891b2' },
	'.svg': { from: '#f59e0b', to: '#d97706' },
};

const MIME_MAP = {
	'.pdf': 'application/pdf',
	'.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'.xls': 'application/vnd.ms-excel',
	'.xlsm': 'application/vnd.ms-excel.sheet.macroenabled.12',
	'.csv': 'text/csv',
	'.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'.doc': 'application/msword',
	'.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'.ppt': 'application/vnd.ms-powerpoint',
	'.mp4': 'video/mp4',
	'.webm': 'video/webm',
	'.mov': 'video/mp4',
	'.ogg': 'video/ogg',
	'.mp3': 'audio/mpeg',
	'.wav': 'audio/wav',
	'.aac': 'audio/aac',
	'.flac': 'audio/flac',
	'.m4a': 'audio/mp4',
	'.jpg': 'image/jpeg',
	'.jpeg': 'image/jpeg',
	'.png': 'image/png',
	'.gif': 'image/gif',
	'.bmp': 'image/bmp',
	'.webp': 'image/webp',
	'.svg': 'image/svg+xml',
};

const ModalDialog = ({ open, title, onClose }) => {
	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const isDark = themeMode === DARK_MODE;
	const { scaleValue } = useResolutionScale();
	const { showPopup } = useMessageContextManager();

	const [selectedFolder, setSelectedFolder] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [filePdfUrl, setFilePdfUrl] = useState(null);
	const [fileVideoUrl, setFileVideoUrl] = useState(null);
	const [fileAudioUrl, setFileAudioUrl] = useState(null);
	const [fileImageUrl, setFileImageUrl] = useState(null);
	const [fileTableData, setFileTableData] = useState(null);
	const [fileExcelBuffer, setFileExcelBuffer] = useState(null);
	const [fileDocHtml, setFileDocHtml] = useState(null);
	const [loginOpen, setLoginOpen] = useState(false);
	const [numPages, setNumPages] = useState(null);
	const [folders, setFolders] = useState([]);
	const [files, setFiles] = useState([]);
	const [currentPathType, setCurrentPathType] = useState('open');
	const [fileText, setFileText] = useState(null);
	const [fileMime, setFileMime] = useState(null);
	const [viewHistory, setViewHistory] = useState([]);
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [imageZoom, setImageZoom] = useState(1);
	const [rawBuffer, setRawBuffer] = useState(null);

	const { username, password } = useSelector((state) => state?.GroupLeaderLogin);

	useEffect(() => {
		return () => {
			if (fileVideoUrl) URL.revokeObjectURL(fileVideoUrl);
			if (filePdfUrl) URL.revokeObjectURL(filePdfUrl);
			if (fileImageUrl) URL.revokeObjectURL(fileImageUrl);
			if (fileAudioUrl) URL.revokeObjectURL(fileAudioUrl);
		};
	}, [fileVideoUrl, filePdfUrl, fileImageUrl, fileAudioUrl]);

	const resetFileStates = () => {
		setSelectedFile(null);
		setFilePdfUrl(null);
		setFileVideoUrl(null);
		setFileAudioUrl(null);
		setFileImageUrl(null);
		setFileTableData(null);
		setFileExcelBuffer(null);
		setFileDocHtml(null);
		setFileText(null);
		setFileMime(null);
		setNumPages(null);
		setRawBuffer(null);
		setImageZoom(1);
	};

	const handleLoginClose = () => setLoginOpen(false);

	useEffect(() => { fetchFolderData(); }, []);

	const fetchFolderData = async () => {
		try {
			const body = { username, password, action: 'GROUP_LEADER', remark: 'view file' };
			const response = await axiosInstance.post(
				`/station/perform-privileged-action/wis/listSubfoldersAndFiles?path=open/`, body,
			);
			const data = response.data;
			setFolders(data?.subFolders || []);
			setFiles(data?.files || []);
			setSelectedFolder(null);
			setCurrentPathType('open');
			resetFileStates();
			if (data.success) {
				if (!data?.subFolders?.length && !data?.files?.length) { showPopup({ error: true, contextText: 'No data found!' }); return; }
				showPopup({ success: true, contextText: 'Data loaded successfully!' });
			}
		} catch (err) {
			showPopup({ error: true, contextText: 'Request Failed!', info: err.message });
		}
	};

	const FetchFile = async (folderName) => {
		try {
			const body = { username, password, action: 'GROUP_LEADER', remark: 'view file' };
			const response = await axiosInstance.post(
				`/station/perform-privileged-action/wis/listSubfoldersAndFiles?path=open/${folderName}`, body,
			);
			const data = response.data;
			setFolders(data?.subFolders || []);
			setFiles(data?.files || []);
			setSelectedFolder(folderName);
			resetFileStates();
		} catch (err) {
			showPopup({ error: true, contextText: 'Request Failed!', info: err.message });
		}
	};

	const FetchLockedFolder = async (folderName) => {
		try {
			if (!password) { showPopup({ error: true, contextText: 'Login required!' }); return; }
			const encryptedPassword = encryptStationPassword(password);
			const body = { username, password: encryptedPassword, action: 'GROUP_LEADER', remark: 'view file' };
			const response = await axiosInstance.post(
				`/station/perform-privileged-action/wis/listSubfoldersAndFiles?path=locked/${folderName}`, body,
			);
			const data = response.data;
			setFolders(data?.subFolders || []);
			setFiles(data?.files || []);
			setSelectedFolder(folderName);
			setCurrentPathType('locked');
			resetFileStates();
		} catch (err) {
			showPopup({ error: true, contextText: 'Locked Request Failed!', info: err.message });
		}
	};

	const fetchFileContent = async (fileName) => {
		try {
			resetFileStates();
			const excelExtensions = ['.xlsx', '.xls', '.xlsm'];
			const csvExtensions = ['.csv'];
			const docxExtensions = ['.docx'];
			const downloadOnlyExtensions = ['.doc', '.ppt', '.pptx', '.zip', '.rar', '.exe', '.bin'];
			const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
			const audioExtensions = ['.mp3', '.wav', '.aac', '.flac', '.m4a'];
			const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
			const fileExt = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
			const isExcelFile = excelExtensions.includes(fileExt);
			const isCsvFile = csvExtensions.includes(fileExt);
			const isDocxFile = docxExtensions.includes(fileExt);
			const isDownloadOnly = downloadOnlyExtensions.includes(fileExt);
			const isVideoFile = videoExtensions.includes(fileExt);
			const isAudioFile = audioExtensions.includes(fileExt);
			const isImageFile = imageExtensions.includes(fileExt);
			let finalPassword = password;

			if (currentPathType === 'locked') {
				if (!password) { showPopup({ error: true, contextText: 'Login required!' }); return; }
				finalPassword = encryptStationPassword(password);
			}

			const body = { username, password: finalPassword, action: 'GROUP_LEADER', remark: 'view file' };
			const basePath = currentPathType === 'locked' ? 'locked' : 'open';
			const fullPath = selectedFolder ? `${basePath}/${selectedFolder}/${fileName}` : `${basePath}/${fileName}`;
			const response = await axiosInstance.post(
				`/station/perform-privileged-action/wis/viewFile?path=${encodeURIComponent(fullPath)}`,
				body,
				{ responseType: 'arraybuffer' },
			);
			const contentTypeHeader = (response.headers && (response.headers['content-type'] || response.headers['Content-Type'])) || null;
			const arrBuffer = response.data;
			if (!arrBuffer) { console.error('Empty response data'); return; }

			const createBlobUrl = (buffer, mime) => URL.createObjectURL(new Blob([buffer], { type: mime || 'application/octet-stream' }));

			setRawBuffer(arrBuffer.slice(0));

			if (isExcelFile) {
				setSelectedFile(fileName);
				setFileExcelBuffer(arrBuffer.slice(0));
				setFileMime('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
				return;
			}
			if (isCsvFile) {
				const csvText = new TextDecoder('utf-8').decode(arrBuffer);
				const result = Papa.parse(csvText, { header: false, skipEmptyLines: true });
				const allRows = result.data;
				if (allRows.length === 0) { showPopup({ error: true, contextText: 'CSV file is empty!' }); return; }
				const headers = allRows[0].map((h, i) => (h !== '' ? String(h) : `Col_${i + 1}`));
				setSelectedFile(fileName);
				setFileTableData({ type: 'csv', headers, rows: allRows.slice(1) });
				setFileMime('text/csv');
				return;
			}
			if (isDocxFile) {
				const result = await mammoth.convertToHtml({ arrayBuffer: arrBuffer });
				setSelectedFile(fileName);
				setFileDocHtml(result.value || '<p>No content found.</p>');
				setFileMime('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
				return;
			}
			if (isDownloadOnly) {
				const mime = contentTypeHeader || 'application/octet-stream';
				const blobUrl = createBlobUrl(arrBuffer, mime);
				const a = document.createElement('a');
				a.href = blobUrl; a.download = fileName;
				document.body.appendChild(a); a.click(); document.body.removeChild(a);
				URL.revokeObjectURL(blobUrl);
				showPopup({ success: true, contextText: `${fileName} downloading!` });
				return;
			}
			if (isVideoFile) {
				const mimeMap = { '.mp4': 'video/mp4', '.webm': 'video/webm', '.ogg': 'video/ogg', '.mov': 'video/mp4' };
				const mime = mimeMap[fileExt] || 'video/mp4';
				setSelectedFile(fileName); setFileVideoUrl(createBlobUrl(arrBuffer, mime)); setFileMime(mime);
				return;
			}
			if (isAudioFile) {
				const mimeMap = { '.mp3': 'audio/mpeg', '.wav': 'audio/wav', '.aac': 'audio/aac', '.flac': 'audio/flac', '.m4a': 'audio/mp4' };
				const mime = mimeMap[fileExt] || 'audio/mpeg';
				setSelectedFile(fileName); setFileAudioUrl(createBlobUrl(arrBuffer, mime)); setFileMime(mime);
				return;
			}
			if (isImageFile) {
				const mimeMap = { '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg', '.png': 'image/png', '.gif': 'image/gif', '.bmp': 'image/bmp', '.webp': 'image/webp', '.svg': 'image/svg+xml' };
				const mime = mimeMap[fileExt] || 'image/jpeg';
				setSelectedFile(fileName); setFileImageUrl(createBlobUrl(arrBuffer, mime)); setFileMime(mime);
				return;
			}
			const uint8 = new Uint8Array(arrBuffer);
			const header = String.fromCharCode(uint8[0] || 0, uint8[1] || 0, uint8[2] || 0, uint8[3] || 0);
			if (header === '%PDF' || (contentTypeHeader && contentTypeHeader.includes('pdf'))) {
				setSelectedFile(fileName); setFilePdfUrl(createBlobUrl(arrBuffer, 'application/pdf')); setFileMime('application/pdf');
				return;
			}
			try {
				const text = new TextDecoder('utf-8').decode(arrBuffer);
				const printableRatio = (text.replace(/[\r\n\t]/g, '').match(/[\x20-\x7E]/g) || []).length / Math.max(1, text.length);
				if ((contentTypeHeader && contentTypeHeader.startsWith('text/')) || printableRatio > 0.6) {
					setSelectedFile(fileName); setFileText(text); setFileMime(contentTypeHeader || 'text/plain');
					return;
				}
			} catch (err) { console.warn('Text decode failed:', err); }
			window.open(createBlobUrl(arrBuffer, contentTypeHeader || 'application/octet-stream'), '_blank');
		} catch (err) {
			console.error('Error opening file:', err);
			showPopup({ error: true, contextText: 'Error!', info: err.message });
		}
	};

	const handleDownload = () => {
		if (!selectedFile || !rawBuffer) return;
		const ext = selectedFile.slice(selectedFile.lastIndexOf('.')).toLowerCase();
		const mime = MIME_MAP[ext] || 'application/octet-stream';
		const blob = new Blob([rawBuffer], { type: mime });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url; a.download = selectedFile;
		document.body.appendChild(a); a.click(); document.body.removeChild(a);
		setTimeout(() => URL.revokeObjectURL(url), 1000);
		showPopup({ success: true, contextText: `Downloading ${selectedFile}` });
	};

	const handleReLogin = async (e, username, password) => {
		e.preventDefault();
		try {
			const body = { username, password, action: 'GROUP_LEADER', remark: 'view file' };
			const response = await axiosInstance.post(
				`/station/perform-privileged-action/wis/listSubfoldersAndFiles?path=locked/`, body,
			);
			const data = response.data;
			setFolders(data?.subFolders || []);
			setFiles(data?.files || []);
			setSelectedFolder('locked');
			setCurrentPathType('locked');
			resetFileStates();
			handleLoginClose();
			if (data.success) {
				if (!data?.subFolders?.length && !data?.files?.length) { showPopup({ error: true, contextText: 'No data found!' }); return; }
				showPopup({ success: true, contextText: 'Data loaded successfully!' });
			}
		} catch (err) {
			showPopup({ error: true, contextText: 'Request Failed!', info: err.message });
		}
	};

	const handleModalBack = () => {
		if (fileVideoUrl) URL.revokeObjectURL(fileVideoUrl);
		if (filePdfUrl) URL.revokeObjectURL(filePdfUrl);
		if (fileImageUrl) URL.revokeObjectURL(fileImageUrl);
		if (fileAudioUrl) URL.revokeObjectURL(fileAudioUrl);
		if (viewHistory.length === 0) { onClose(); return; }
		const last = viewHistory[viewHistory.length - 1];
		setViewHistory((prev) => prev.slice(0, -1));
		setSelectedFolder(last.selectedFolder);
		setFolders(last.folders);
		setFiles(last.files);
		resetFileStates();
	};

	if (!open) return null;

	const breadcrumbs = [
		{ label: 'Home', icon: <HomeIcon sx={{ fontSize: 13 }} /> },
		...(selectedFolder ? [{ label: selectedFolder }] : []),
		...(selectedFile ? [{ label: selectedFile }] : []),
	];

	// ── Theme tokens ──────────────────────────────────────────────────────────
	const bg = isDark ? '#0a0f1e' : '#f0f4f9';
	const surface = isDark ? '#111827' : '#ffffff';
	const surfaceElev = isDark ? '#1a2235' : '#f8faff';
	const border = isDark ? '#1f2d45' : '#e2e8f0';
	const accent = '#3b82f6';
	const accentLight = isDark ? '#1e3a5f' : '#eff6ff';
	const textPrimary = isDark ? '#e2e8f0' : '#0f172a';
	const textSecondary = isDark ? '#64748b' : '#6b7280';
	const sidebarBg = isDark ? '#0d1526' : '#f8faff';

	const ext = selectedFile?.slice(selectedFile?.lastIndexOf('.'))?.toLowerCase();
	const extColors = EXT_COLOR_MAP[ext] || { from: '#3b6fcf', to: '#1a56db' };

	return (
		<>
			<Modal
				open={open}
				onClose={onClose}
				sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
			>
				<Box
					onClick={(e) => e.stopPropagation()}
					sx={{
						width: '100vw',
						height: '100vh',
						background: bg,
						borderRadius: 0,
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
						outline: 'none',
					}}
				>
					{/* ── Rainbow accent strip ──────────────────────────────────── */}
					<Box sx={{
						height: '3px',
						flexShrink: 0,
						background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899, #f59e0b, #10b981, #3b82f6)',
						backgroundSize: '200% 100%',
						'@keyframes shimmer': {
							'0%': { backgroundPosition: '0% 0%' },
							'100%': { backgroundPosition: '200% 0%' },
						},
						animation: 'shimmer 4s linear infinite',
					}} />

					{/* ── Header ───────────────────────────────────────────────── */}
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						px: 2,
						py: 1.2,
						borderBottom: `1px solid ${border}`,
						background: surface,
						flexShrink: 0,
						gap: 1,
					}}>
						{/* Sidebar toggle */}
						<Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
							<IconButton
								onClick={() => setSidebarOpen(!sidebarOpen)}
								size="small"
								sx={{ color: textSecondary, borderRadius: '8px', '&:hover': { background: accentLight, color: accent } }}
							>
								<MenuIcon fontSize="small" />
							</IconButton>
						</Tooltip>

						{/* Back button */}
						<Tooltip title="Go back">
							<IconButton
								onClick={handleModalBack}
								size="small"
								sx={{
									color: textPrimary,
									background: accentLight,
									borderRadius: '8px',
									'&:hover': { background: accent, color: '#fff' },
									transition: 'all 0.2s',
								}}
							>
								<ArrowBackIcon fontSize="small" />
							</IconButton>
						</Tooltip>

						{/* Breadcrumbs */}
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
							{breadcrumbs.map((crumb, i) => (
								<React.Fragment key={i}>
									{i > 0 && <ChevronRightIcon sx={{ fontSize: 13, color: textSecondary }} />}
									<Typography sx={{
										fontSize: '12px',
										fontWeight: i === breadcrumbs.length - 1 ? 700 : 400,
										color: i === breadcrumbs.length - 1 ? textPrimary : textSecondary,
										display: 'flex', alignItems: 'center', gap: 0.4,
										maxWidth: i === breadcrumbs.length - 1 ? '260px' : undefined,
										overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
									}}>
										{crumb.icon}{crumb.label}
									</Typography>
								</React.Fragment>
							))}
						</Box>

						{/* Spacer */}
						<Box sx={{ flex: 1 }} />

						{/* Title */}
						<Typography sx={{
							fontSize: '15px', fontWeight: 800, color: textPrimary,
							letterSpacing: '-0.3px', whiteSpace: 'nowrap',
						}}>
							{title}
						</Typography>

						<Box sx={{ flex: 1 }} />

						{/* Download button */}
						{selectedFile && rawBuffer && (
							<Tooltip title="Download file">
								<Button
									variant="contained"
									size="small"
									startIcon={<DownloadIcon />}
									onClick={handleDownload}
									sx={{
										background: `linear-gradient(135deg, ${extColors.from}, ${extColors.to})`,
										borderRadius: '8px',
										textTransform: 'none',
										fontWeight: 600,
										fontSize: '12px',
										px: 1.5,
										py: 0.6,
										boxShadow: `0 4px 14px ${extColors.from}55`,
										'&:hover': { opacity: 0.9, boxShadow: `0 6px 20px ${extColors.from}77` },
									}}
								>
									Download
								</Button>
							</Tooltip>
						)}

						{/* Close button */}
						<Tooltip title="Close">
							<IconButton
								onClick={onClose}
								size="small"
								sx={{
									color: textSecondary,
									borderRadius: '8px',
									ml: 0.5,
									'&:hover': { background: '#ef444422', color: '#ef4444' },
								}}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>

					{/* ── Body ─────────────────────────────────────────────────── */}
					<Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

						{/* ── Sidebar ──────────────────────────────────────────── */}
						<Box sx={{
							width: sidebarOpen ? '256px' : '0px',
							transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
							overflow: 'hidden',
							flexShrink: 0,
							borderRight: `1px solid ${border}`,
							background: sidebarBg,
						}}>
							<Box sx={{ width: '256px', height: '100%', display: 'flex', flexDirection: 'column', py: 2, px: 1.5, gap: 0.5, overflow: 'hidden auto' }}>

								{/* Brand chip */}
								<Box sx={{ px: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
									<Box sx={{
										width: 32, height: 32, borderRadius: '8px',
										background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
										display: 'flex', alignItems: 'center', justifyContent: 'center',
										flexShrink: 0, boxShadow: '0 4px 12px rgba(59,130,246,0.4)',
									}}>
										<InsertDriveFileIcon sx={{ fontSize: 16, color: '#fff' }} />
									</Box>
									<Box>
										<Typography sx={{ fontSize: '13px', fontWeight: 700, color: textPrimary, lineHeight: 1.2 }}>WIS Documents</Typography>
										<Typography sx={{ fontSize: '10px', color: textSecondary }}>Secure File Viewer</Typography>
									</Box>
								</Box>

								<Typography sx={{ fontSize: '9px', fontWeight: 800, color: textSecondary, textTransform: 'uppercase', letterSpacing: '1.5px', px: 1.5, mb: 0.5 }}>
									Storage
								</Typography>

								{/* Open documents */}
								<SidebarItem
									icon={<FolderOpenIcon sx={{ fontSize: 17 }} />}
									label="Open Documents"
									active={currentPathType === 'open' && !selectedFile}
									accent={accent}
									accentLight={accentLight}
									textSecondary={textSecondary}
									onClick={() => { fetchFolderData(); setViewHistory([]); }}
								/>

								{/* Locked documents */}
								<SidebarItem
									icon={<LockIcon sx={{ fontSize: 17 }} />}
									label="Locked Documents"
									active={currentPathType === 'locked' && !selectedFile}
									accent="#d97706"
									accentLight={isDark ? '#3a2000' : '#fffbeb'}
									textSecondary={textSecondary}
									onClick={() => setLoginOpen(true)}
								/>

								{/* Subfolders */}
								{folders.length > 0 && (
									<>
										<Typography sx={{ fontSize: '9px', fontWeight: 800, color: textSecondary, textTransform: 'uppercase', letterSpacing: '1.5px', px: 1.5, mt: 1.5, mb: 0.5 }}>
											Folders
										</Typography>
										{folders.map((folder, i) => (
											<SidebarItem
												key={i}
												icon={<FolderOpenIcon sx={{ fontSize: 15 }} />}
												label={folder}
												active={selectedFolder === folder}
												accent={accent}
												accentLight={accentLight}
												textSecondary={textSecondary}
												onClick={() => {
													setViewHistory((prev) => [...prev, { type: 'folder', selectedFolder, folders, files }]);
													currentPathType === 'locked' ? FetchLockedFolder(folder) : FetchFile(folder);
												}}
											/>
										))}
									</>
								)}
							</Box>
						</Box>

						{/* ── Main Content ─────────────────────────────────────── */}
						<Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

							{/* ── File Viewer (full height) ── */}
							{selectedFile && (
								<Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

									{/* Viewer toolbar */}
									<Box sx={{
										display: 'flex', alignItems: 'center', gap: 2,
										px: 3, py: 1.5,
										borderBottom: `1px solid ${border}`,
										background: surface,
										flexShrink: 0,
									}}>
										<Box sx={{
											width: 44, height: 44, borderRadius: '12px',
											background: `linear-gradient(135deg, ${extColors.from}, ${extColors.to})`,
											display: 'flex', alignItems: 'center', justifyContent: 'center',
											boxShadow: `0 6px 18px ${extColors.from}55`,
											flexShrink: 0,
										}}>
											{getFileIcon(selectedFile, 22)}
										</Box>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography sx={{ fontWeight: 700, fontSize: '14px', color: textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
												{selectedFile}
											</Typography>
											<Typography sx={{ fontSize: '11px', color: textSecondary }}>{fileMime || 'Document'}</Typography>
										</Box>

										{/* Image zoom controls */}
										{fileImageUrl && (
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Tooltip title="Zoom out">
													<IconButton size="small" onClick={() => setImageZoom(z => Math.max(z - 0.25, 0.25))} sx={{ color: textSecondary, borderRadius: '8px', '&:hover': { background: accentLight, color: accent } }}>
														<ZoomOutIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Chip label={`${Math.round(imageZoom * 100)}%`} size="small" sx={{ fontWeight: 700, fontSize: '11px', background: accentLight, color: accent, height: 24 }} />
												<Tooltip title="Zoom in">
													<IconButton size="small" onClick={() => setImageZoom(z => Math.min(z + 0.25, 5))} sx={{ color: textSecondary, borderRadius: '8px', '&:hover': { background: accentLight, color: accent } }}>
														<ZoomInIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Reset zoom">
													<IconButton size="small" onClick={() => setImageZoom(1)} sx={{ color: textSecondary, borderRadius: '8px', '&:hover': { background: accentLight, color: accent } }}>
														<RestartAltIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</Box>
										)}
									</Box>

									{/* ── Actual viewer — fills all remaining height ── */}
									<Box sx={{ flex: 1, minHeight: 0, overflow: (fileVideoUrl || fileImageUrl) ? 'hidden' : 'auto', position: 'relative' }}>

										{/* ── PDF ── */}
										{filePdfUrl && (
											<Box sx={{
												display: 'flex', flexDirection: 'column', alignItems: 'center',
												p: 4, gap: 2,
												background: isDark ? '#0d1117' : '#e8e8e8',
												minHeight: '100%',
											}}>
												<Document
													file={filePdfUrl}
													onLoadSuccess={({ numPages }) => setNumPages(numPages)}
													onLoadError={(e) => console.error('PDF load error:', e)}
												>
													{Array.from({ length: numPages || 0 }, (_, i) => (
														<Box key={`page_${i + 1}`} sx={{
															mb: 3, borderRadius: '8px', overflow: 'hidden',
															boxShadow: isDark
																? '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)'
																: '0 8px 32px rgba(0,0,0,0.18)',
														}}>
															<Page
																pageNumber={i + 1}
																width={Math.min(window.innerWidth * (sidebarOpen ? 0.62 : 0.78), 1100)}
																renderTextLayer={false}
																renderAnnotationLayer={false}
															/>
														</Box>
													))}
												</Document>
											</Box>
										)}

										{/* ── Plain Text ── */}
										{fileText && (
											<Box sx={{ p: 3, height: '100%', boxSizing: 'border-box' }}>
												<Box sx={{
													p: 3, background: surface, borderRadius: '12px',
													border: `1px solid ${border}`,
													boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.06)',
												}}>
													<Typography component="pre" sx={{
														whiteSpace: 'pre-wrap',
														fontFamily: '"JetBrains Mono","Fira Code",monospace',
														fontSize: '13px', color: textPrimary, lineHeight: 1.75,
													}}>
														{fileText}
													</Typography>
												</Box>
											</Box>
										)}

										{/* ── Video — Cinema Mode ── */}
										{fileVideoUrl && (
											<Box sx={{
												width: '100%', height: '100%',
												background: '#000',
												display: 'flex', alignItems: 'center', justifyContent: 'center',
											}}>
												<video
													key={fileVideoUrl}
													controls
													autoPlay
													src={fileVideoUrl}
													style={{ width: '100%', height: '100%', objectFit: 'contain' }}
													onError={(e) => console.error('Video error:', e)}
												/>
											</Box>
										)}

										{/* ── Audio — Atmospheric Player ── */}
										{fileAudioUrl && (
											<Box sx={{
												width: '100%', height: '100%',
												background: isDark
													? 'radial-gradient(ellipse at 50% 30%, #1a0540 0%, #0a0118 60%, #000 100%)'
													: 'radial-gradient(ellipse at 50% 30%, #f3e8ff 0%, #ede9fe 40%, #e0e7ff 100%)',
												display: 'flex', flexDirection: 'column',
												alignItems: 'center', justifyContent: 'center',
												gap: 4, p: 4,
												position: 'relative', overflow: 'hidden',
											}}>
												{/* Decorative ambient circles */}
												{[180, 280, 380].map((size, i) => (
													<Box key={i} sx={{
														position: 'absolute',
														width: size, height: size,
														borderRadius: '50%',
														border: `1px solid rgba(168,85,247,${0.12 - i * 0.03})`,
														top: '50%', left: '50%',
														transform: 'translate(-50%, -70%)',
													}} />
												))}

												{/* Pulsing glow orb */}
												<Box sx={{
													width: 130, height: 130, borderRadius: '50%',
													background: 'linear-gradient(135deg, #c084fc, #7c3aed, #4f46e5)',
													display: 'flex', alignItems: 'center', justifyContent: 'center',
													position: 'relative', zIndex: 1,
													boxShadow: '0 0 60px rgba(168,85,247,0.6), 0 0 120px rgba(168,85,247,0.3)',
													'@keyframes glow': {
														'0%,100%': { boxShadow: '0 0 50px rgba(168,85,247,0.5), 0 0 100px rgba(168,85,247,0.25)' },
														'50%': { boxShadow: '0 0 90px rgba(168,85,247,0.9), 0 0 180px rgba(168,85,247,0.5)' },
													},
													animation: 'glow 2.5s ease-in-out infinite',
												}}>
													<AudioFileIcon sx={{ fontSize: 60, color: '#fff', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))' }} />
												</Box>

												<Typography sx={{
													fontSize: '18px', fontWeight: 700, zIndex: 1,
													color: isDark ? '#f3e8ff' : '#4c1d95',
													textShadow: isDark ? '0 2px 12px rgba(168,85,247,0.5)' : 'none',
													maxWidth: 500, textAlign: 'center',
													overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
												}}>
													{selectedFile}
												</Typography>

												{/* Animated waveform bars */}
												<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', height: 80, zIndex: 1 }}>
													{WAVE_HEIGHTS.map((h, i) => (
														<Box key={i} sx={{
															width: 5,
															background: `linear-gradient(to top, rgba(168,85,247,0.9), rgba(196,132,252,0.6))`,
															borderRadius: '3px',
															'@keyframes wave': {
																'0%,100%': { height: `${Math.max(h * 0.3, 8)}px` },
																'50%': { height: `${h}px` },
															},
															animation: `wave ${WAVE_DURATIONS[i]} ease-in-out infinite`,
															animationDelay: `${i * 0.06}s`,
														}} />
													))}
												</Box>

												<Box sx={{ width: '100%', maxWidth: 600, zIndex: 1 }}>
													<audio
														key={fileAudioUrl}
														controls
														autoPlay
														src={fileAudioUrl}
														style={{
															width: '100%',
															borderRadius: '12px',
															filter: isDark ? 'invert(1) hue-rotate(180deg)' : 'none',
														}}
														onError={(e) => console.error('Audio error:', e)}
													/>
												</Box>
											</Box>
										)}

										{/* ── Image — Gallery Mode ── */}
										{fileImageUrl && (
											<Box sx={{
												width: '100%', height: '100%',
												background: isDark ? '#050508' : '#1a1a1a',
												display: 'flex', alignItems: 'center', justifyContent: 'center',
												overflow: 'auto',
												cursor: imageZoom > 1 ? 'grab' : 'zoom-in',
											}}>
												<img
													src={fileImageUrl}
													alt={selectedFile}
													onClick={() => imageZoom === 1 ? setImageZoom(2) : setImageZoom(1)}
													style={{
														transform: `scale(${imageZoom})`,
														transformOrigin: 'center center',
														transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
														maxWidth: imageZoom <= 1 ? '100%' : 'none',
														maxHeight: imageZoom <= 1 ? '100%' : 'none',
														objectFit: 'contain',
														borderRadius: imageZoom <= 1 ? '4px' : '0',
														display: 'block',
													}}
												/>
											</Box>
										)}

										{/* ── Excel / SpreadsheetViewer ── */}
										{fileExcelBuffer && (
											<Box sx={{ width: '100%', height: '100%' }}>
												<SpreadsheetViewer
													arrayBuffer={fileExcelBuffer}
													fileName={selectedFile}
													isDark={isDark}
												/>
											</Box>
										)}

										{/* ── CSV table ── */}
										{fileTableData?.type === 'csv' && (
											<Box sx={{ p: 3, height: '100%', boxSizing: 'border-box' }}>
												<Box sx={{
													borderRadius: '12px', overflow: 'hidden',
													border: `1px solid ${border}`,
													boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,0,0,0.06)',
												}}>
													<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
														<thead>
															<tr>
																{fileTableData.headers.map((header, i) => (
																	<th key={i} style={{
																		background: `linear-gradient(135deg, #10b981, #059669)`,
																		color: '#fff', padding: '12px 18px', textAlign: 'left',
																		fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1,
																		borderRight: '1px solid rgba(255,255,255,0.15)', fontSize: '12px',
																		letterSpacing: '0.4px', textTransform: 'uppercase',
																	}}>
																		{header}
																	</th>
																))}
															</tr>
														</thead>
														<tbody>
															{fileTableData.rows.map((row, rowIndex) => (
																<tr key={rowIndex} style={{
																	backgroundColor: rowIndex % 2 === 0
																		? (isDark ? '#111827' : '#ffffff')
																		: (isDark ? '#1a2235' : '#f0fdf4'),
																	transition: 'background 0.15s',
																}}>
																	{fileTableData.headers.map((_, colIndex) => (
																		<td key={colIndex} style={{
																			padding: '10px 18px',
																			borderBottom: `1px solid ${border}`,
																			borderRight: `1px solid ${border}`,
																			color: textPrimary,
																			whiteSpace: 'nowrap', fontSize: '13px',
																		}}>
																			{row[colIndex] ?? ''}
																		</td>
																	))}
																</tr>
															))}
														</tbody>
													</table>
												</Box>
											</Box>
										)}

										{/* ── DOCX viewer ── */}
										{fileDocHtml && (
											<Box sx={{ p: 4, height: '100%', boxSizing: 'border-box' }}>
												<Box sx={{
													maxWidth: 900, mx: 'auto',
													background: isDark ? '#111827' : '#ffffff',
													borderRadius: '16px',
													border: `1px solid ${border}`,
													p: { xs: 3, md: 6 },
													boxShadow: isDark ? '0 8px 40px rgba(0,0,0,0.5)' : '0 8px 40px rgba(0,0,0,0.08)',
												}}>
													<Box
														sx={{
															color: isDark ? '#e2e8f0' : '#1a202c',
															fontFamily: '"Calibri","Segoe UI",Arial,sans-serif',
															fontSize: '15px', lineHeight: 1.9,
															'& h1,& h2,& h3,& h4,& h5,& h6': {
																fontWeight: 700, marginTop: '1.4em', marginBottom: '0.5em',
																color: isDark ? '#f1f5f9' : '#0f172a',
															},
															'& h1': { fontSize: '2em', borderBottom: `2px solid ${border}`, pb: 1 },
															'& h2': { fontSize: '1.5em' },
															'& p': { marginBottom: '0.9em' },
															'& table': { borderCollapse: 'collapse', width: '100%', marginBottom: '1.2em', borderRadius: '8px', overflow: 'hidden' },
															'& td,& th': { border: `1px solid ${border}`, padding: '8px 14px' },
															'& th': { background: isDark ? '#1e2d45' : '#f0f7ff', fontWeight: 700 },
															'& ul,& ol': { paddingLeft: '1.8em', marginBottom: '0.9em' },
															'& li': { marginBottom: '0.3em' },
															'& strong': { fontWeight: 700 },
															'& em': { fontStyle: 'italic' },
															'& img': { maxWidth: '100%', borderRadius: '8px' },
															'& blockquote': {
																borderLeft: `4px solid ${accent}`,
																pl: 2, ml: 0, color: textSecondary,
																fontStyle: 'italic',
															},
														}}
														dangerouslySetInnerHTML={{ __html: fileDocHtml }}
													/>
												</Box>
											</Box>
										)}

										{/* ── Fallback ── */}
										{!filePdfUrl && !fileText && !fileVideoUrl && !fileAudioUrl && !fileTableData && !fileExcelBuffer && !fileDocHtml && !fileImageUrl && fileMime && (
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
												<Box sx={{ textAlign: 'center', p: 4 }}>
													<InsertDriveFileIcon sx={{ fontSize: 64, color: textSecondary, opacity: 0.4, mb: 2 }} />
													<Typography sx={{ color: textSecondary, fontSize: '14px', mb: 2 }}>
														Preview not available for this file type.
													</Typography>
													<Button variant="contained" onClick={handleDownload} startIcon={<DownloadIcon />}
														sx={{ background: `linear-gradient(135deg, ${extColors.from}, ${extColors.to})`, textTransform: 'none', borderRadius: '10px' }}>
														Download {selectedFile}
													</Button>
												</Box>
											</Box>
										)}
									</Box>
								</Box>
							)}

							{/* ── Grid Browser ───────────────────────────────────── */}
							{!selectedFile && (
								<Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
									{/* Grid sub-header */}
									<Box sx={{
										px: 3, py: 1.5,
										borderBottom: `1px solid ${border}`,
										background: surface,
										flexShrink: 0,
										display: 'flex', alignItems: 'center', justifyContent: 'space-between',
									}}>
										<Typography sx={{ fontSize: '13px', color: textSecondary }}>
											<span style={{ fontWeight: 600, color: textPrimary }}>{folders.length + files.length}</span>
											{' '}item{folders.length + files.length !== 1 ? 's' : ''}
											{selectedFolder ? ` in "${selectedFolder}"` : ''}
										</Typography>
										{currentPathType === 'locked' && (
											<Chip label="Locked" size="small" icon={<LockIcon sx={{ fontSize: '12px !important' }} />}
												sx={{ background: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '10px', height: 22 }} />
										)}
									</Box>

									<Box
										className={clsx(styles.scrollBar)}
										sx={{ flex: 1, overflow: 'auto', p: 3 }}
									>
										<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2 }}>

											{/* Folders */}
											{folders.map((folder, index) => (
												<GridCard
													key={`folder-${index}`}
													icon={<FolderOpenIcon sx={{ fontSize: 30, color: '#fff' }} />}
													label={folder}
													sublabel="Folder"
													gradientFrom="#fbbf24"
													gradientTo="#f59e0b"
													surface={surface}
													border={border}
													textPrimary={textPrimary}
													textSecondary={textSecondary}
													accentLight={accentLight}
													accent={accent}
													isDark={isDark}
													onClick={() => {
														setViewHistory((prev) => [...prev, { type: 'folder', selectedFolder, folders, files }]);
														currentPathType === 'locked' ? FetchLockedFolder(folder) : FetchFile(folder);
													}}
												/>
											))}

											{/* Files */}
											{files.map((file, index) => {
												const fileExt = file.slice(file.lastIndexOf('.')).toLowerCase();
												const colors = EXT_COLOR_MAP[fileExt] || { from: '#3b6fcf', to: '#1a56db' };
												return (
													<GridCard
														key={`file-${index}`}
														icon={getFileIcon(file, 28)}
														label={file}
														sublabel={fileExt.replace('.', '').toUpperCase()}
														gradientFrom={colors.from}
														gradientTo={colors.to}
														surface={surface}
														border={border}
														textPrimary={textPrimary}
														textSecondary={textSecondary}
														accentLight={accentLight}
														accent={accent}
														isDark={isDark}
														onClick={() => {
															setViewHistory((prev) => [...prev, { type: 'fileList', selectedFolder, folders, files }]);
															fetchFileContent(file);
														}}
													/>
												);
											})}

											{/* Empty state */}
											{folders.length === 0 && files.length === 0 && (
												<Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, color: textSecondary, gap: 2 }}>
													<Box sx={{
														width: 80, height: 80, borderRadius: '20px',
														background: isDark ? '#1a2235' : '#f0f4ff',
														display: 'flex', alignItems: 'center', justifyContent: 'center',
													}}>
														<InsertDriveFileIcon sx={{ fontSize: 40, opacity: 0.3 }} />
													</Box>
													<Typography sx={{ fontSize: '15px', fontWeight: 600, opacity: 0.5 }}>No files found</Typography>
													<Typography sx={{ fontSize: '13px', opacity: 0.4 }}>This folder is empty</Typography>
												</Box>
											)}
										</Box>
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			</Modal>

			<ReLogin open={loginOpen} handleClose={handleLoginClose} handleReLogin={handleReLogin} />
		</>
	);
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SidebarItem = ({ icon, label, active, accent, accentLight, textSecondary, onClick }) => (
	<Box
		onClick={onClick}
		sx={{
			display: 'flex', alignItems: 'center', gap: 1.5,
			px: 1.5, py: 0.9, borderRadius: '10px', cursor: 'pointer',
			background: active ? accentLight : 'transparent',
			color: active ? accent : textSecondary,
			transition: 'all 0.15s',
			'&:hover': { background: accentLight, color: accent },
		}}
	>
		{icon}
		<Typography sx={{ fontSize: '13px', fontWeight: active ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
			{label}
		</Typography>
		{active && (
			<Box sx={{ ml: 'auto', width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
		)}
	</Box>
);

const GridCard = ({ icon, label, sublabel, gradientFrom, gradientTo, surface, border, textPrimary, textSecondary, accentLight, accent, isDark, onClick }) => (
	<Box
		onClick={onClick}
		sx={{
			display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
			gap: 1.2, p: 2.5, borderRadius: '16px', cursor: 'pointer',
			background: surface, border: `1px solid ${border}`,
			transition: 'all 0.22s cubic-bezier(0.4,0,0.2,1)',
			'&:hover': {
				transform: 'translateY(-4px) scale(1.02)',
				boxShadow: isDark
					? `0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px ${gradientFrom}44`
					: `0 12px 32px rgba(0,0,0,0.12), 0 0 0 1px ${gradientFrom}44`,
				borderColor: gradientFrom,
			},
		}}
	>
		<Box sx={{
			width: 62, height: 62, borderRadius: '14px',
			background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
			display: 'flex', alignItems: 'center', justifyContent: 'center',
			boxShadow: `0 6px 18px ${gradientFrom}55`,
			transition: 'transform 0.22s',
			'&:hover': { transform: 'scale(1.08)' },
		}}>
			{icon}
		</Box>
		<Typography sx={{
			fontSize: '12px', fontWeight: 600, color: textPrimary,
			textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis',
			whiteSpace: 'nowrap', maxWidth: '100%', lineHeight: 1.3,
		}}>
			{label}
		</Typography>
		<Typography sx={{
			fontSize: '10px', color: textSecondary,
			textTransform: 'uppercase', letterSpacing: '0.8px',
			background: `${gradientFrom}22`,
			px: 1, py: 0.3, borderRadius: '4px', fontWeight: 600,
		}}>
			{sublabel}
		</Typography>
	</Box>
);

export default ModalDialog;
